import { useState } from "react"
import PropTypes from "prop-types"
import { Link, Router, Redirect, navigate } from "@reach/router"
import { HardReload, SideNav, BlankState } from "components/ui"
import { Button } from "@planningcenter/tapestry-react"
import Attendees from "./Attendees"
import Areas from "./Areas"
import ManageAreas from "./ManageAreas"
import { attendeeAssignmentAreaBy, filterAttendees } from "./assignments"
import { api } from "utils/api"
import sortBy from "utils/sorter"
import { isBlank, arrayToObject } from "utils/helpers"
import { IntegrationEnabledAlert } from "../CheckInsStations/IntegrationEnabledAlert"

const mapAttendees = (filterType, attendees, areas, assignmentTypeId) => {
  return filterAttendees(filterType, Object.values(attendees), assignmentTypeId).map(attendee => ({
    ...attendee,
    assignedArea: attendeeAssignmentAreaBy("typeId", assignmentTypeId, attendee, areas)
  }))
}

const AssignmentType = ({
  event,
  routerBasePath,
  checkinsUrl,
  assignmentAreasPath,
  eventStartDate,
  gradeOptions,
  genderOptions,
  assignmentAreas,
  assignmentType: assignment,
  attendees: assignmentAttendees,
  attendeeTypes,
  apiBaseUrl,
  checkInsBaseUrl,
  signupFeatures = [],
  subscribedToCheckIns,
  checkInsSettingsPath
}) => {
  const [assignmentType, setAssignmentType] = useState(assignment)
  const [attendees, setAttendees] = useState(arrayToObject(assignmentAttendees))
  const [areas, setAreas] = useState(arrayToObject(assignmentAreas))

  const url = path => `${assignmentType.paths.show}/${path}`

  const unassignedAttendees = mapAttendees("unassigned", attendees, areas, assignmentType.id)

  const assignedAttendees = mapAttendees("assigned", attendees, areas, assignmentType.id)

  const navItems = {
    attendees: [
      {
        text: "All Attendees",
        subText: [...unassignedAttendees, ...assignedAttendees].length,
        linkTo: url("attendees/all")
      },
      {
        text: "Assigned",
        subText: assignedAttendees.length,
        linkTo: url("attendees/assigned")
      },
      {
        text: "Unassigned",
        subText: unassignedAttendees.length,
        linkTo: url("attendees/unassigned")
      }
    ],
    areas: [
      {
        text: assignmentType.name,
        type: "header",
        children: (
          <Button as={Link} to={url(`areas/manage`)} size="xs" variant="outline" title="Manage" />
        )
      },
      ...sortBy("position", Object.values(areas)).map(area => ({
        text: area.name,
        subText: `${area.capacity - area.capacityRemaining} / ${area.capacity}`,
        linkTo: url(`areas/${area.id}`)
      }))
    ]
  }

  const handleApiError = error => {
    let text
    if (error?.status == 404) {
      text = "The assignment area could not be found"
    } else {
      text = ""
    }

    alertModal(
      {
        text,
        title: "An error occured",
        type: "error",
        showCancelButton: true,
        cancelButtonText: "Cancel",
        confirmButtonText: "View all assignments"
      },
      () => {
        window.location.assign(url("attendees/all"))
      }
    )
  }

  const handleAttendeeUnassign = (selectedAttendee, area = null) => {
    return api
      .post(area ? area.paths.unassign : assignmentType.paths.unassignAll, {
        attendee_ids: selectedAttendee.map(a => a.id),
        assignment_type_id: assignmentType.id
      })
      .then(resp => {
        setAttendees({ ...attendees, ...arrayToObject(resp.attendees) })
        setAreas({ ...areas, ...arrayToObject(resp.areas) })

        return resp
      })
      .catch(handleApiError)
  }

  const handleUnassignAllAlert = onSaveClick => {
    return alertModal(
      {
        title: "Unassign all?",
        text: "Are you sure you want to unassign all attendees? This action cannot be reversed",
        type: "warning",
        showCancelButton: true,
        cancelButtonText: "Cancel",
        confirmButtonText: "Confirm",
        customClass: "destructive-warning",
        closeOnConfirm: true
      },
      () => onSaveClick()
    )
  }

  const handleAttendeeAssign = (selectedAttendee, area) => {
    return api
      .post(area.paths.assign, {
        attendee_ids: [selectedAttendee.id]
      })
      .then(resp => {
        setAttendees({ ...attendees, ...arrayToObject(resp.attendees) })
        setAreas({ ...areas, ...arrayToObject(resp.areas) })

        return resp
      })
      .catch(handleApiError)
  }

  const handleAutoAssign = strategy => {
    return api
      .post(assignmentType.paths.autoAssign, { strategy: strategy || "greedy" })
      .then(resp => {
        setAttendees({ ...attendees, ...arrayToObject(resp.attendees) })
        setAreas({ ...areas, ...arrayToObject(resp.areas) })

        return resp
      })
      .catch(handleApiError)
  }

  const handleAssignmentAreaCreate = params => {
    return api
      .post(assignmentType.paths.assignmentAreasCreate, {
        assignment_area: {
          ...params,
          position: Object.values(areas).length + 1,
          assignment_type_id: assignmentType.id
        }
      })
      .then(resp => {
        setAreas({ ...areas, [resp.data.id]: resp.data })
      })
      .catch(handleApiError)
  }

  const handleAssignmentAreaDelete = path => {
    return api
      .del(path)
      .then(resp => {
        setAreas(arrayToObject(resp.data))
        return resp
      })
      .catch(handleApiError)
  }

  const handleAssignmentAreaUpdate = (area, params) => {
    return api
      .put(area.paths.show, {
        assignment_area: {
          ...params,
          assignment_type_id: assignmentType.id
        }
      })
      .then(resp => {
        navigate(-1)
        setAreas({ ...areas, [resp.data.id]: resp.data })
      })
      .catch(handleApiError)
  }

  const handleDragEnd = newItems => {
    return api
      .put(assignmentAreasPath, {
        assignment_area_ids: newItems.map(x => x.id),
        assignment_type_id: assignmentType.id
      })
      .then(resp => setAreas(arrayToObject(resp.data)))
      .catch(_ => setAreas(areas))
  }

  const handleNewAreaClick = () => window.location.assign(url("areas/manage/new"))

  let basePath = `${routerBasePath}assignment_types/:assignmentTypeId`

  return (
    <>
      <IntegrationEnabledAlert apiBaseUrl={apiBaseUrl} eventId={event.id} />
      <div className="d-f@md">
        {!isBlank(areas) && <SideNav navItems={navItems} />}
        <main className="f-1 o-h">
          <Router primary={false}>
            {isBlank(areas) ? (
              <BlankState
                path={`${basePath}`}
                imageURL="/static/blank-states/registrations-assignment-areas.svg"
                onButtonClick={handleNewAreaClick}
                buttonLabel="New assignment area"
                buttonAttributes={{ "data-pendo": "new_assignment_area" }}
                placeholderText={`You're almost ready to assign people to ${assignmentType.name}!  Add areas for specific cabins, classes, or teams you're assigning people to.`}
                helpLinkURL="https://pcoregistrations.zendesk.com/hc/en-us/articles/216724228-Assignments"
                helpLinkText="What are assignments?"
              />
            ) : (
              <Redirect from={`/${basePath}`} to={`/${basePath}/attendees/all`} noThrow />
            )}
            <HardReload path="/*" />
            <Attendees
              path={`${basePath}/attendees/:attendeeType`}
              assignmentTypePaths={assignmentType.paths}
              assignedAttendees={assignedAttendees}
              unassignedAttendees={unassignedAttendees}
              areas={areas}
              onAttendeeAssign={handleAttendeeAssign}
              onAttendeeUnassign={handleAttendeeUnassign}
              onUnassignAllAlert={handleUnassignAllAlert}
              onAutoAssign={handleAutoAssign}
              checkinsUrl={checkinsUrl}
              event={event}
              apiBaseUrl={apiBaseUrl}
              checkInsBaseUrl={checkInsBaseUrl}
              assignmentTypes={[assignmentType]}
              signupFeatures={signupFeatures}
              subscribedToCheckIns={subscribedToCheckIns}
              checkInsSettingsPath={checkInsSettingsPath}
            />
            <Areas
              path={`${basePath}/areas/:areaId/*`}
              basePath={url("areas")}
              allAttendeesPath={url("attendees/all")}
              areas={areas}
              attendees={[...assignedAttendees, ...unassignedAttendees]}
              onAttendeeAssign={handleAttendeeAssign}
              onAttendeeUnassign={handleAttendeeUnassign}
              onUnassignAllAlert={handleUnassignAllAlert}
              onAreaUpdate={handleAssignmentAreaUpdate}
              gradeOptions={gradeOptions}
              genderOptions={genderOptions}
              eventStartDate={eventStartDate}
              signupFeatures={signupFeatures}
            />
            <ManageAreas
              path={`${basePath}/areas/manage/*`}
              basePath={assignmentType.paths.show}
              eventStartDate={eventStartDate}
              areas={areas}
              gradeOptions={gradeOptions}
              genderOptions={genderOptions}
              onCreate={handleAssignmentAreaCreate}
              onDelete={handleAssignmentAreaDelete}
              onDragEnd={handleDragEnd}
              assignmentType={assignmentType}
              onAssignmentTypeSave={setAssignmentType}
              attendeeTypes={attendeeTypes}
              signupFeatures={signupFeatures}
            />
          </Router>
        </main>
      </div>
    </>
  )
}

AssignmentType.propTypes = {
  routerBasePath: PropTypes.string,
  registrationsUrl: PropTypes.string,
  assignmentAreasPath: PropTypes.string,
  eventStartDate: PropTypes.string,
  apiBaseUrl: PropTypes.string,
  checkInsBaseUrl: PropTypes.string,
  checkinsUrl: PropTypes.string,
  gradeOptions: PropTypes.shape({}),
  genderOptions: PropTypes.shape({}),
  assignmentType: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    paths: PropTypes.shape({
      show: PropTypes.string,
      autoAssign: PropTypes.string,
      unassignAll: PropTypes.string,
      assignmentAreasCreate: PropTypes.string
    })
  }),
  assignmentAreas: PropTypes.array,
  attendees: PropTypes.array,
  attendeeTypes: PropTypes.array,
  event: PropTypes.shape({
    id: PropTypes.number,
    signupIntegration: PropTypes.shape({
      integration_id: PropTypes.string,
      id: PropTypes.string
    })
  }),
  signupFeatures: PropTypes.array,
  subscribedToCheckIns: PropTypes.bool,
  checkInsSettingsPath: PropTypes.string
}

export default AssignmentType
